import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  TextField,
  Button,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Snackbar,
  Alert,
  useTheme,
  Link as MuiLink
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Phone,
  Email,
  LocationOn,
  Facebook,
  Instagram,
  Build,
  Architecture,
  HomeRepairService,
  Engineering,
  Send,
  PhoneAndroid,
  Call
} from '@mui/icons-material';
import MusicNoteIcon from '@mui/icons-material/MusicNote'; // TikTok replacement
import { Link } from 'react-router-dom';
import { getAllServices, submitMessage } from '../utils/api';

const FooterContainer = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, #232526 0%, #414345 100%)`,
  color: theme.palette.primary.contrastText,
  position: 'relative',
  overflow: 'hidden',
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(4),
  boxShadow: '0 0 40px 0 rgba(0,0,0,0.6)',
  zIndex: 2,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-60px',
    left: '-60px',
    width: '320px',
    height: '320px',
    background: 'radial-gradient(circle, #ffb34733 0%, #23252600 80%)',
    opacity: 0.7,
    zIndex: 1,
    filter: 'blur(8px)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-80px',
    right: '-80px',
    width: '400px',
    height: '400px',
    background: 'radial-gradient(circle, #00c6ff33 0%, #41434500 80%)',
    opacity: 0.6,
    zIndex: 1,
    filter: 'blur(12px)',
  },
}));

const FooterLink = styled(Link)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  textDecoration: 'none',
  transition: 'color 0.3s ease',
  '&:hover': {
    color: theme.palette.primary.light,
  },
}));

const SocialIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: theme.palette.primary.light,
  },
}));

const Footer = () => {
  const theme = useTheme();
  const [services, setServices] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await getAllServices();
        const servicesData = response?.data?.services || response?.services || [];
        setServices(Array.isArray(servicesData) ? servicesData.slice(0, 4) : []);
      } catch (err) {
        console.error('Failed to load services for footer', err);
      }
    };

    fetchServices();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      setSnackbarMessage('Please fill in all required fields');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    try {
      setLoading(true);
      const response = await submitMessage({
        ...formData,
        mobile: 'From Footer', // Adding a placeholder since mobile is required by API
      });

      if (response.success) {
        setSnackbarMessage('Message sent successfully! We will get back to you soon.');
        setSnackbarSeverity('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSnackbarMessage('Message sent successfully! We will get back to you soon.');
        setSnackbarSeverity('success');
        setFormData({ name: '', email: '', message: '' });
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setSnackbarSeverity('error');
    } finally {
      setLoading(false);
      setOpenSnackbar(true);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const serviceIcons = {
    'construction': <Build />,
    'architecture': <Architecture />,
    'repair': <HomeRepairService />,
    'engineering': <Engineering />
  };

  const getServiceIcon = (index) => {
    const icons = Object.values(serviceIcons);
    return icons[index % icons.length];
  };

  return (
    <FooterContainer>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 3 }}>
        <Grid container spacing={4}>
          {/* Company Information */}
          <Grid item xs={12} md={3}>
            <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.contrastText, fontWeight: 'bold' }}>
              All In One Construction
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.primary.contrastText, mb: 2 }}>
              Your trusted partner in construction. We bring your vision to life with quality craftsmanship and dedication.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <SocialIconButton aria-label="facebook"
                component="a"
                href="https://www.facebook.com/profile.php?id=61578199235565&rdid=bUK35ypmxLQoax68&share_url=https%3A%2F%2Fwww.facebook.com%2Fshare%2F19Aq8AFVUS%2F#"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Facebook />
              </SocialIconButton>
              <SocialIconButton
                aria-label="instagram"
                component="a"
                href="https://www.instagram.com/all.inoneconstruction/?source=omni_redirect"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Instagram />
              </SocialIconButton>
              <SocialIconButton aria-label="tiktok"
                component="a"
                href="https://www.tiktok.com/@all.in.one.constru"
                target="_blank"
                rel="noopener noreferrer"
              >
                <MusicNoteIcon />
              </SocialIconButton>

              {/* <SocialIconButton aria-label="linkedin">
                <LinkedIn />
              </SocialIconButton> */}
            </Box>
          </Grid>

          {/* Services */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.contrastText, fontWeight: 'bold' }}>
              Our Services
            </Typography>
            <List dense sx={{ p: 0 }}>
              {services.length > 0 ? (
                services.map((service, index) => (
                  <ListItem key={service.id} disableGutters sx={{ pb: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30, color: theme.palette.primary.light }}>
                      {getServiceIcon(index)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <FooterLink to={`/services/${service.id}`}>
                          {service.title}
                        </FooterLink>
                      }
                    />
                  </ListItem>
                ))
              ) : (
                <>
                  <ListItem disableGutters sx={{ pb: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30, color: theme.palette.primary.light }}>
                      <Build />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <FooterLink to="/services">
                          Construction Services
                        </FooterLink>
                      }
                    />
                  </ListItem>
                  <ListItem disableGutters sx={{ pb: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30, color: theme.palette.primary.light }}>
                      <Architecture />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <FooterLink to="/services">
                          Architectural Design
                        </FooterLink>
                      }
                    />
                  </ListItem>
                  <ListItem disableGutters sx={{ pb: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30, color: theme.palette.primary.light }}>
                      <HomeRepairService />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <FooterLink to="/services">
                          Renovation & Repairs
                        </FooterLink>
                      }
                    />
                  </ListItem>
                  <ListItem disableGutters sx={{ pb: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30, color: theme.palette.primary.light }}>
                      <Engineering />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <FooterLink to="/services">
                          Engineering Solutions
                        </FooterLink>
                      }
                    />
                  </ListItem>
                </>
              )}
            </List>
            <Button
              component={Link}
              to="/services"
              variant="outlined"
              size="small"
              sx={{
                mt: 2,
                color: theme.palette.primary.light,
                borderColor: theme.palette.primary.light,
                '&:hover': {
                  borderColor: theme.palette.primary.contrastText,
                  color: theme.palette.primary.contrastText,
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              View All Services
            </Button>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.contrastText, fontWeight: 'bold' }}>
              Contact Us
            </Typography>
            <List dense sx={{ p: 0 }}>
              <ListItem disableGutters sx={{ pb: 1 }}>
                <ListItemIcon sx={{ minWidth: 30, color: theme.palette.primary.light }}>
                  <LocationOn />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ color: theme.palette.primary.contrastText }}>
                      30 Mossford Green BARKINGSIDE, IG6 2BJ, UK
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem disableGutters sx={{ pb: 1 }}>
                <ListItemIcon sx={{ minWidth: 30, color: theme.palette.primary.light }}>
                  <Email />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <MuiLink
                      href="mailto:info@allinoneconstructions.com"
                      sx={{
                        color: theme.palette.primary.contrastText,
                        textDecoration: 'none',
                        '&:hover': { color: theme.palette.primary.light }
                      }}
                    >
                      info@allinoneconstructions.com
                    </MuiLink>
                  }
                />
              </ListItem>
              <ListItem disableGutters sx={{ pb: 1 }}>
                <ListItemIcon sx={{ minWidth: 30, color: theme.palette.primary.light }}>
                  <PhoneAndroid />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <MuiLink
                      href="tel:+447931937835"
                      sx={{
                        color: theme.palette.primary.contrastText,
                        textDecoration: 'none',
                        '&:hover': { color: theme.palette.primary.light }
                      }}
                    >
                      +44 7931 937835
                    </MuiLink>
                  }
                />
              </ListItem>
              <ListItem disableGutters sx={{ pb: 1 }}>
                <ListItemIcon sx={{ minWidth: 30, color: theme.palette.primary.light }}>
                  <Call />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <MuiLink
                      href="tel:+4420 3535 6266"
                      sx={{
                        color: theme.palette.primary.contrastText,
                        textDecoration: 'none',
                        '&:hover': { color: theme.palette.primary.light }
                      }}
                    >
                      020 3535 6266
                    </MuiLink>
                  }
                />
              </ListItem>
            </List>
            <Button
              component={Link}
              to="/contact"
              variant="outlined"
              size="small"
              sx={{
                mt: 2,
                color: theme.palette.primary.light,
                borderColor: theme.palette.primary.light,
                '&:hover': {
                  borderColor: theme.palette.primary.contrastText,
                  color: theme.palette.primary.contrastText,
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              Contact Page
            </Button>
          </Grid>

          {/* Quick Contact Form */}
          <Grid item xs={12} md={3}>
            <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.contrastText, fontWeight: 'bold' }}>
              Quick Message
            </Typography>
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                margin="dense"
                size="small"
                required
                sx={{
                  mb: 1.5,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(249, 247, 247, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.light,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.light,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(249, 247, 247, 0.7)',
                  },
                  '& .MuiInputBase-input': {
                    color: theme.palette.primary.contrastText,
                  },
                }}
              />
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                margin="dense"
                size="small"
                required
                sx={{
                  mb: 1.5,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(249, 247, 247, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.light,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.light,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(249, 247, 247, 0.7)',
                  },
                  '& .MuiInputBase-input': {
                    color: theme.palette.primary.contrastText,
                  },
                }}
              />
              <TextField
                fullWidth
                label="Message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                margin="dense"
                size="small"
                multiline
                rows={3}
                required
                sx={{
                  mb: 1.5,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(249, 247, 247, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.light,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.light,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(249, 247, 247, 0.7)',
                  },
                  '& .MuiInputBase-input': {
                    color: theme.palette.primary.contrastText,
                  },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                startIcon={<Send />}
                sx={{
                  mt: 1,
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                }}
              >
                {loading ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: 'rgba(249, 247, 247, 0.1)', background: 'rgba(255,255,255,0.08)', borderRadius: 2 }} />

        {/* Copyright and Bottom Links */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'center',
          alignItems: { xs: 'center', sm: 'center' },
          textAlign: { xs: 'center', sm: 'left' },
          gap: 2,
          position: 'relative',
          zIndex: 3,
        }}>
          <Typography variant="body2" sx={{ color: 'rgba(249, 247, 247, 0.7)' }}>
            © {new Date().getFullYear()} All In One Construction Ltd. All Rights Reserved.
          </Typography>
          {/* <Box sx={{ 
            display: 'flex', 
            gap: 3,
            flexWrap: 'wrap',
            justifyContent: { xs: 'center', sm: 'flex-end' }
          }}>
            <FooterLink to="/" sx={{ fontSize: '0.875rem', color: 'rgba(249, 247, 247, 0.7)' }}>
              Privacy Policy
            </FooterLink>
            <FooterLink to="/" sx={{ fontSize: '0.875rem', color: 'rgba(249, 247, 247, 0.7)' }}>
              Terms of Service
            </FooterLink>
            <FooterLink to="/" sx={{ fontSize: '0.875rem', color: 'rgba(249, 247, 247, 0.7)' }}>
              Sitemap
            </FooterLink>
          </Box> */}
        </Box>
      </Container>

      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%', background: 'linear-gradient(90deg, #232526 60%, #00c6ff 100%)', color: '#fff' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </FooterContainer>
  );
};

export default Footer;